<template>
  <div class="pc__image-swiper" ref="imageCarouselRef" v-if="enableImageCarousel">
    <div class="pc__slide-wrapper">
      <div class="pc__slide" v-for="(image, index) in featureImageVariants" :key="index">
        <transition name="fade" mode="out-in">
          <ImageField
            class="pc__feature-img"
            :obj="{ url: image.src, alt: image.alt, width: 600, height: 750 }"
            :isLoadingLazy="isLoadingLazy"
            :key="image.src"
          />
        </transition>
      </div>
    </div>
    <div class="swiper-pagination"></div>
  </div>

  <template v-else>
    <transition name="fade" mode="out-in">
      <ImageField
        class="pc__feature-img"
        :obj="{ url: featureImage.src, alt: product.title, width: 600, height: 750 }"
        :isLoadingLazy="isLoadingLazy"
        :key="featureImage.src"
      />
    </transition>
    <ImageField
      :key="hoverImage ? hoverImage.src : featureImage.src"
      class="pc__hover-img"
      :obj="{ url: hoverImage ? hoverImage.src : featureImage.src, alt: product.title, width: 600, height: 750 }"
      :isLoadingLazy="isLoadingLazy"
    />
  </template>
</template>

<script setup>
  import { ref, computed, onMounted, watch, toRefs } from 'vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs, Pagination, A11y } from 'swiper/core';
  import ImageField from '../image.vue';

  SwiperCore.use([Navigation, EffectFade, Thumbs, Pagination, A11y]);

  const ON_MODEL_VS_FLATLAY_VARIANT_ID = '019ed556-80e9-75fe-b8da-075031f312b5';

  const props = defineProps({
    enableImageCarousel: {
      type: Boolean,
      default: false,
    },
    selectedColor: {
      type: String,
      default: '',
    },
    useLifestyleImage: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    isLoadingLazy: {
      type: Boolean,
      default: false,
    },
  });

  const { enableImageCarousel, product, isLoadingLazy, selectedColor, useLifestyleImage } = toRefs(props);

  const imageCarouselRef = ref(null);
  const imageSwiper = ref(null);
  const isOnModelImageTestActive = ref(
    typeof window.__stioOnModelVsFlatlayTestActive === 'boolean' ? window.__stioOnModelVsFlatlayTestActive : false
  );

  onMounted(() => {
    setOnModelImageTestState();

    if (enableImageCarousel.value) {
      initializeSwiper();
    }
  });

  watch(enableImageCarousel, (newVal) => {
    if (newVal) {
      initializeSwiper();
    } else if (imageSwiper.value) {
      imageSwiper.value.destroy();
    }
  });

  const featureImageVariants = computed(() => {
    const { images, title } = product.value;

    if (shouldUseOnModelImage.value) {
      return [onModelImage.value];
    }

    return window.getProductImages(images, selectedColor.value, title).slice(0, 4);
  });

  const featureImage = computed(() => {
    const { images, title } = product.value;

    if (shouldUseOnModelImage.value) {
      return onModelImage.value;
    }

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];
    const lifestyleImage = imageData.find((image) => image.alt && image.alt.toLowerCase().includes('alternative'));

    if (useLifestyleImage.value && lifestyleImage) {
      return lifestyleImage;
    }

    return defaultImage;
  });

  const hoverImage = computed(() => {
    const { images, title } = product.value;

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];

    if (shouldUseOnModelImage.value) {
      return defaultImage;
    }

    const filteredImageData = imageData.filter((image) => image.alt && image.alt.toLowerCase().includes('alternative'));
    const defaultHoverImage = filteredImageData[0];

    if (useLifestyleImage.value) {
      return defaultImage;
    }

    return defaultHoverImage;
  });

  const selectedColorVariant = computed(() => {
    const variants = product.value.variants || [];
    const normalizedSelectedColor = selectedColor.value?.toLowerCase();

    if (!normalizedSelectedColor) {
      return variants.find((variant) => variant.on_model_image?.src);
    }

    const selectedColorVariants = variants.filter((variant) => {
      return [variant.color, variant.option1].some((value) => value?.toLowerCase() === normalizedSelectedColor);
    });

    return (
      selectedColorVariants.find((variant) => variant.available && variant.on_model_image?.src) ||
      selectedColorVariants.find((variant) => variant.on_model_image?.src)
    );
  });

  const onModelImage = computed(() => selectedColorVariant.value?.on_model_image);

  const shouldUseOnModelImage = computed(() => {
    return isOnModelImageTestActive.value && onModelImage.value?.src;
  });

  const setOnModelImageTestState = async () => {
    isOnModelImageTestActive.value = await getOnModelImageTestState();
  };

  const AB_TEST_COLLECTION_HANDLES = new Set([
    'best-sellers',
    'bike',
    'hike',
    'jackets',
    'jackets-and-vests',
    'mens',
    'mens-bestsellers',
    'mens-bike',
    'mens-hike',
    'mens-jackets',
    'mens-jackets-vests',
    'mens-pants',
    'mens-run',
    'mens-shorts',
    'mens-swim',
    'mens-upf-sun-protection',
    'mens-vests',
    'pants',
    'run',
    'shorts',
    'stio-x-seea',
    'swim',
    'upf-sun-protection',
    'vests',
    'womens',
    'womens-bestsellers',
    'womens-bike',
    'womens-hike',
    'womens-jackets',
    'womens-jackets-vests',
    'womens-pants',
    'womens-run',
    'womens-shorts',
    'womens-swim',
    'womens-upf-sun-protection',
    'womens-vests',
  ]);

  const isEligibleCollectionPage = () => {
    const match = window.location.pathname.match(/^\/collections\/([^/?#]+)/);
    return match ? AB_TEST_COLLECTION_HANDLES.has(match[1]) : false;
  };

  const getOnModelImageTestState = async () => {
    if (!isEligibleCollectionPage() || !window.shoplift) {
      return false;
    }

    if (typeof window.__stioOnModelVsFlatlayTestActive === 'boolean') {
      return window.__stioOnModelVsFlatlayTestActive;
    }

    if (!window.__stioOnModelVsFlatlayTestPromise) {
      window.__stioOnModelVsFlatlayTestPromise = window.shoplift
        .isHypothesisActive(ON_MODEL_VS_FLATLAY_VARIANT_ID)
        .then(Boolean)
        .catch(() => false)
        .then((active) => {
          window.__stioOnModelVsFlatlayTestActive = active;
          return active;
        });
    }

    return window.__stioOnModelVsFlatlayTestPromise;
  };

  const initializeSwiper = () => {
    imageSwiper.value = new Swiper(imageCarouselRef.value, {
      slidesPerView: 1,
      wrapperClass: 'pc__slide-wrapper',
      slideClass: 'pc__slide',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: {
        enabled: true,
      },
    });
  };
</script>
